import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as _ from 'lodash';

import { AnalyticsBrowser, InitOptions } from '@segment/analytics-next';
import { isSegmentEnabled, segmentKey } from 'config';
import { logging } from 'helpers/monitoring';
import { getRoleNameFromPrivileges } from 'helpers/team-user-roles';
import { User } from 'services/authenticatedUser/types';
import {
    TrackingEvents,
    TrackingEventsWithPayloads,
    convertTrackingPayloadToGtmPayload,
    gtmTrackingEventName
} from 'services/tracking/gtm';
import { WindowService } from 'services/window.service';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

function trackingEventErrorBoundary<T>(eventName, action: () => T | null) {
    try {
        return action();
    } catch (error) {
        logging.error(`Failed to track: ${eventName}`, { error });
    }
}

@Injectable({
    providedIn: 'root'
})
export class SegmentService {
    private previousUrl: string;
    private analytics: AnalyticsBrowser | null = null;

    constructor(
        private router: Router,
        private windowService: WindowService
    ) {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd && !_.isUndefined(event.url)),
                takeUntilDestroyed()
            )
            .subscribe((event: NavigationEnd) => {
                this.page(event.url, this.previousUrl);
                this.previousUrl = event.url;
            });
    }

    // https://github.com/segmentio/analytics-next/tree/master/packages/browser#lazy--delayed-loading
    load(options: InitOptions = {}) {
        if (!this.analytics) {
            this.analytics = new AnalyticsBrowser();
        }

        this.analytics
            .load(
                {
                    writeKey: segmentKey
                },
                options
            )
            .catch((error) => {
                logging.error('Error loading Segment', error);
            });
    }

    track(eventName, properties?) {
        trackingEventErrorBoundary(eventName, () => {
            if (this.isEnabled()) {
                const user = this.analytics.instance?.user();
                const options = user ? { context: { traits: user.traits() } } : {};
                if (properties?.ecommerce) {
                    this.resetGtmDataLayer();
                }
                this.analytics.track(eventName, properties, options);
            }
        });
    }

    trackGtm<T extends TrackingEvents>(name: T, payload: TrackingEventsWithPayloads[T]) {
        trackingEventErrorBoundary(name, () => {
            this.resetGtmDataLayer();
            this.track(gtmTrackingEventName, convertTrackingPayloadToGtmPayload(name, payload));
        });
    }

    page(url: string, referrer: string) {
        if (this.isEnabled()) {
            this.analytics.page({ url, referrer });
        }
    }

    identify(user, traits) {
        if (this.isEnabled()) {
            this.analytics.identify(user._id, traits);
        }
    }

    identifyUser(user) {
        const traits = this.getTraitsDataForUser(user);
        this.identify(user, traits);
    }

    trackViewedBrandContent(brandId, brandName, source, contentType) {
        const segmentData = {
            brandId,
            brandName,
            source,
            contentType
        };

        this.track('Viewed brand content', segmentData);
    }

    trackExportedCsv(retailerOrganizationId, retailerOrganizationName) {
        const segmentData = {
            retailerOrganizationId,
            retailerOrganizationName
        };

        this.track('Exported CSV', segmentData);
    }

    getTraitsDataForUser(userData: User) {
        return {
            email: userData.email,
            preferredLanguage: userData.preferredLanguage,
            name: userData.firstname + ' ' + userData.surname,
            firstName: userData.firstname,
            lastName: userData.surname,
            referrer: (userData as any).referrer,
            organization: (userData.organization || {}).name,
            // the lists array will contain information about mailing list id
            // that users will be added to in 'ActiveCampaign tool'
            lists: [{ id: '22', status: 'active' }],
            role: getRoleNameFromPrivileges(userData.privileges, userData.organization?.type),
            company: {
                company_id: userData.organization._id,
                name: userData.organization.name,
                plan: userData.organization.plan,
                company_type: userData.organization.type,
                phone: userData.organization.phone,
                address1: userData.organization.address.line1,
                address2: userData.organization.address.line2,
                zip: userData.organization.address.zip,
                city: userData.organization.address.city,
                country: userData.organization.address.country,
                package_reorder: userData.organization.packages.reorder,
                package_ecommerce: userData.organization.packages.ecommerce,
                package_request_marketing_files: userData.organization.packages.requestMarketingFiles,
                package_unrestricted_downloads: userData.organization.packages.unrestrictedDownloads,
                package_api: userData.organization.packages.api
            },
            beta: environment.beta
        };
    }

    isEnabled() {
        return isSegmentEnabled() && this.analytics?.instance?.initialized;
    }

    resetSegment() {
        this.windowService.intercom?.('shutdown');
        this.windowService.drift?.reset?.();
        if (this.analytics) {
            this.analytics.reset();
        }
    }

    /**
     * Resets the GTM datalayer of all payload data added by events tracked before calling this method.
     * This is necessary in SPA/"in-memory routed" apps as there is no page reload to clear the datalayer.
     * If this is not done, the datalayer is polluted with values from previous events.
     *
     * @remarks
     * This method uses the reset process described in DataLayer docs in
     * https://developers.google.com/tag-platform/devguides/datalayer#reset
     *
     * @returns void
     */
    private resetGtmDataLayer() {
        if (this.windowService.dataLayer) {
            this.windowService.dataLayer.push(function () {
                this.reset();
            });
        }
    }
}
